import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Camera, Download, Share2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const BrandedPhotoUpload = ({ onPhotoSubmit, title = "Upload Your Fresh Cut" }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [brandedImage, setBrandedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image is too large. Please select an image under 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('Image loaded successfully');
        setSelectedImage(e.target.result);
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const applyWatermark = useCallback(() => {
    if (!selectedImage) return;

    setIsProcessing(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Load the main image
    const img = new Image();
    
    // Add timeout for main image loading
    const imageTimeout = setTimeout(() => {
      console.error('Image loading timeout');
      setIsProcessing(false);
      toast.error('Image processing timeout. Please try a smaller image.');
    }, 10000); // 10 second timeout
    
    img.onload = () => {
      clearTimeout(imageTimeout);
      console.log('Main image loaded for canvas:', img.width, 'x', img.height);
      
      // Set canvas size to match image
      const maxWidth = 1200;
      const maxHeight = 1200;
      let width = img.width;
      let height = img.height;

      // Resize if needed
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw main image
      ctx.drawImage(img, 0, 0, width, height);
      console.log('Main image drawn on canvas');

      // Add branded frame overlay (subtle border)
      const frameWidth = 20;
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, 'rgba(132, 204, 22, 0.3)'); // Lime green
      gradient.addColorStop(1, 'rgba(147, 51, 234, 0.3)'); // Purple
      
      // Top border
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, frameWidth);
      // Bottom border
      ctx.fillRect(0, height - frameWidth, width, frameWidth);
      // Left border
      ctx.fillRect(0, 0, frameWidth, height);
      // Right border
      ctx.fillRect(width - frameWidth, 0, frameWidth, height);

      // Add Sauce Sity logo sticker in bottom-right corner
      // Falls back to text watermark if the logo image fails to load (e.g. CORS)
      const drawTextFallback = () => {
        ctx.font = 'bold 32px Arial';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillText('SAUCE SITY', width - 280, height - 40);
        ctx.fillStyle = 'rgba(132, 204, 22, 1)';
        ctx.fillText('SAUCE SITY', width - 282, height - 42);
      };

      const finalize = () => {
        try {
          const brandedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
          setBrandedImage(brandedDataUrl);
          setIsProcessing(false);
          console.log('Photo branded successfully');
          toast.success('Photo branded! Looking fresh! 🔥');
        } catch (err) {
          console.error('Error creating branded image:', err);
          setIsProcessing(false);
          toast.error('Failed to process image');
        }
      };

      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        // Sticker ~25% of the photo's shorter side, capped at 320px for big shots
        const stickerSize = Math.min(Math.min(width, height) * 0.25, 320);
        const padding = Math.max(24, stickerSize * 0.12);
        // Position: TOP-RIGHT
        const cx = width - stickerSize / 2 - padding;   // sticker center X
        const cy = stickerSize / 2 + padding;           // sticker center Y
        const tiltRad = (-8 * Math.PI) / 180;           // slight -8° tilt

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(tiltRad);

        // Soft dark drop shadow so the sticker pops on busy backgrounds
        ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
        ctx.shadowBlur = 18;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 6;
        ctx.drawImage(logo, -stickerSize / 2, -stickerSize / 2, stickerSize, stickerSize);

        ctx.restore();

        finalize();
      };
      logo.onerror = () => {
        console.warn('Logo image failed to load — using text watermark fallback');
        drawTextFallback();
        finalize();
      };
      // Source: Sauce Sity drippy SS sticker logo with transparent background
      logo.src = '/sauce-sity-logo-transparent.png';
      // Early return — finalize() is called from onload / onerror
      return;
    };

    img.onerror = (error) => {
      clearTimeout(imageTimeout);
      console.error('Image load error:', error);
      setIsProcessing(false);
      toast.error('Failed to process image. Please try a different photo.');
    };

    console.log('Loading selected image...');
    img.src = selectedImage;
  }, [selectedImage]);

  useEffect(() => {
    if (selectedImage) {
      applyWatermark();
    }
  }, [selectedImage, applyWatermark]);

  const handleDownload = () => {
    if (!brandedImage) return;

    const link = document.createElement('a');
    link.download = `sauce-sity-fresh-cut-${Date.now()}.jpg`;
    link.href = brandedImage;
    link.click();
    toast.success('Photo downloaded! Share it on social media! 📱');
  };

  const handleShare = async () => {
    if (!brandedImage) return;

    try {
      // Convert data URL to blob
      const response = await fetch(brandedImage);
      const blob = await response.blob();
      const file = new File([blob], 'sauce-sity-fresh-cut.jpg', { type: 'image/jpeg' });

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'My Fresh Cut at Sauce Sity',
          text: 'Check out my fresh cut from Sauce Sity Barbershop! 💈🔥',
          files: [file]
        });
        toast.success('Thanks for sharing! 🙌');
      } else {
        // Fallback - copy to clipboard or download
        handleDownload();
        toast.info('Photo ready to share! Upload to your socials! 📸');
      }
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Share not available on this device');
    }
  };

  const handleSubmit = async () => {
    if (!brandedImage) return;
    
    setIsProcessing(true);
    
    try {
      if (onPhotoSubmit) {
        // Call the parent's onPhotoSubmit which will handle API call
        await onPhotoSubmit(brandedImage);
      }
      resetUpload();
    } catch (error) {
      console.error('Error submitting photo:', error);
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetUpload = () => {
    setSelectedImage(null);
    setBrandedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-lime-400 flex items-center gap-2">
          <Camera className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!selectedImage ? (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="photo-upload"
            />
            <label htmlFor="photo-upload">
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center cursor-pointer hover:border-lime-400 transition-colors">
                <Upload className="w-12 h-12 mx-auto mb-4 text-zinc-500" />
                <p className="text-white mb-2">Click to upload your fresh cut</p>
                <p className="text-zinc-500 text-sm">
                  Your photo will be branded with the Sauce Sity logo
                </p>
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="relative">
              {brandedImage && (
                <img
                  src={brandedImage}
                  alt="Branded preview"
                  className="w-full h-auto rounded-lg"
                />
              )}
              {isProcessing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-2"></div>
                    <p>Adding Sauce Sity branding...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Hidden canvas for processing */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Actions */}
            {brandedImage && (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleDownload}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={handleShare}
                  className="bg-lime-500 hover:bg-lime-600 text-black"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                {onPhotoSubmit && (
                  <Button
                    onClick={handleSubmit}
                    disabled={isProcessing}
                    className="col-span-2 bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {isProcessing ? 'Uploading...' : 'Upload to Gallery'}
                  </Button>
                )}
                <Button
                  onClick={resetUpload}
                  variant="outline"
                  className="col-span-2 border-zinc-700 text-zinc-300"
                >
                  <X className="w-4 h-4 mr-2" />
                  Choose Different Photo
                </Button>
              </div>
            )}

            <div className="bg-zinc-800 p-3 rounded-lg border border-zinc-700">
              <p className="text-zinc-400 text-sm">
                ✨ Your photo now features the Sauce Sity logo! Perfect for sharing on social media.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BrandedPhotoUpload;
